import React, { useRef, useState, type RefObject } from 'react';
import { DateRange, type RangeKeyDict } from 'react-date-range';
import { format, addDays } from 'date-fns';
import useOnClickOutside from 'use-onclickoutside';
import { usePopupPosition } from '../../hooks';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Button from '../Button';
import { CalendarIcon } from '../../assets/icons';

type DatePickerInputProps = {
  value?: Date[];
  onChange?: (value: Date[]) => void;
  label?: string;
  formatDate?: string;
  buttonClassName?: string;
};

export const DateRangeInput: React.FC<DatePickerInputProps> = ({
  label,
  formatDate = 'MMM d, yyyy',
  buttonClassName = 'w-full px-4 py-2 border rounded-full text-left bg-white shadow-sm hover:border-gray-400 focus:outline-none',
}) => {
  const [tempRange, setTempRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: 'selection',
    },
  ]);

  const [finalRange, setFinalRange] = useState(tempRange);
  const [showPicker, setShowPicker] = useState(false);

  const inputRef = useRef<HTMLDivElement>(null);
  const { popupRef, anchorRef, popupPosition, anchorPosition } =
    usePopupPosition();

  useOnClickOutside(inputRef as unknown as RefObject<HTMLElement>, () =>
    setShowPicker(false)
  );

  const handleSelect = (ranges: RangeKeyDict) => {
    const { startDate, endDate } = ranges.selection;

    if (startDate && endDate) {
      setTempRange([
        {
          startDate,
          endDate,
          key: 'selection',
        },
      ]);
    }
  };

  const handleApply = () => {
    setFinalRange(tempRange);
    setShowPicker(false);
  };

  const handleCancel = () => {
    setTempRange(finalRange);
    setShowPicker(false);
  };

  const formattedLabel = `${format(finalRange[0].startDate!, formatDate)} - ${format(finalRange[0].endDate!, formatDate)}`;

  return (
    <div
      ref={node => {
        node && anchorRef(node);
        inputRef.current = node;
      }}
      className="relative w-full max-w-md mx-auto"
    >
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <button
        onClick={() => setShowPicker(prev => !prev)}
        className={buttonClassName}
      >
        <div className="flex items-center gap-x-2 text-secondary">
          <CalendarIcon width={18} height={18} />
          {formattedLabel}
        </div>
      </button>

      {showPicker && (
        <div
          ref={popupRef}
          style={{
            left: popupPosition?.style?.left ?? 0,
            top: popupPosition?.style?.top ?? 0,
            minWidth: anchorPosition.width ?? 0,
          }}
          className="fixed z-popper w-fit max-w-full py-sm bg-white rounded-md shadow-lg"
        >
          <DateRange
            ranges={tempRange}
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            showDateDisplay={false}
            rangeColors={['#8358fa']}
            months={2}
            direction="horizontal"
            className="text-tiny"
          />
          <div className="flex justify-end gap-x-4 items-center p-3 border-t mt-1">
            <Button
              label="Cancel"
              size="small"
              type="contrast"
              onClick={handleCancel}
            />
            <Button
              label="Apply"
              size="small"
              onClick={handleApply}
              disabled={!tempRange[0].startDate || !tempRange[0].endDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
